import { memo, useCallback, useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import {
  Box,
  FieldSet,
  FormHelp,
  Heading,
  Label,
  LabeledControl,
  Legend,
  Paragraph,
  Radio,
  TextButton,
} from '@zillow/constellation-10';

import { debounce } from '@zg-rentals/zdash';

import PropertiesActions from '../../../../actions/PropertiesActions';
import actions from '../../../../containers/CreateListingPage/actions';
import { useForm, useGuardedMultiEdit } from '../../../../hooks';
import { useMultiplexListingFlow } from '../../../../hooks/queries/multiplex';
import { useSelector } from '../../../../selectors/useSelector';
import { postingConstraints } from '../../../../utils/formValidation';
import { multiFieldsUtil, SAVE_TO_PARENT_KEY } from '../../../../utils/postingUtils';
import wizardActions from '../../../Wizard/actions';

import TimeConstants from '../../../../constants/TimeConstants';
import { DISPLAY_SERVER_ERROR_AT } from '../../../../constants/WizardConstants';
import { RENTERS_INSURANCE_KEY } from '../../../../containers/CreateListingPage/constants';
import RentersInsuranceCSEvents, {
  RENTERS_INSURANCE_CS_TRIGGER_LOCATIONS,
} from '../../../../model/ClickstreamEvent/RentersInsuranceCSEvents';
import RentersInsurance from '../../../../model/RentersInsurance';
import WizardServerError from '../../../../model/WizardServerError';

import { ModularListingContext } from '../../../../containers/CreateListingPage/context';

import RMWPopover from '../../../RMWPopover';
import { WizardContext } from '../../../Wizard/context';
import WizardAlert from '../../../WizardAlert';

import type { multiSaveProps } from '../../../../utils/postingUtils';

const propTypes = {};
const defaultProps = {};

const radioControlName = 'isRentersInsuranceRequired';

// T0D0: move to rental-manager-env stringHelper
const stringToBoolean = (value: string) => {
  return value.toLowerCase() === 'true';
};

const RentersInsuranceFieldSet = () => {
  const dispatch = useDispatch();

  const { reducerWizardDispatch } = useContext(WizardContext);
  const {
    modularListingState: { isInModalState, isSaveComplete, serverErrorObj, shouldSaveOnBlur },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const { isInMultiplexListingFlow } = useMultiplexListingFlow();

  const {
    properties: {
      currentProperty,
      currentProperty: {
        listingAlias,
        rentersInsurance: { isRentersInsuranceRequired },
      },
    },
    user,
  } = useSelector((state) => state);

  const {
    inputs,
    errorObj,
    hasFieldError,
    handleChange: handleFormChange,
  } = useForm(
    {
      isRentersInsuranceRequired,
    },
    postingConstraints,
  );

  const { saveMultipleFieldsPropertyInfo } = useGuardedMultiEdit();

  const triggerLocation = isInModalState
    ? RENTERS_INSURANCE_CS_TRIGGER_LOCATIONS.POSTING_PATH_REVIEW
    : RENTERS_INSURANCE_CS_TRIGGER_LOCATIONS.POSTING_PATH;

  useEffect(() => {
    triggerImpressionCSEvent();
  }, []);

  useEffect(() => {
    if (isSaveComplete) {
      save({
        // @ts-expect-error
        target: { name: radioControlName, value: inputs.isRentersInsuranceRequired },
      });
    }
  }, [isSaveComplete]);

  const clearFieldError = () => {
    actions.updateHasFieldError({ reducerDispatch, hasFieldError: false });
  };

  const clearServerError = () => {
    if (serverErrorObj.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  const save = (event: { target: { value: null | boolean | string; name: string } }) => {
    if (isInMultiplexListingFlow) {
      // TODO: handle save for multiplex listing flow
      return;
    }

    const { value, name: fieldName } = event.target;

    // field error check needs to be above must click on cta check
    if (hasFieldError(fieldName, value)) {
      actions.updateHasFieldError({ reducerDispatch, hasFieldError: true });
      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });

      return Promise.resolve(false);
    }

    // return if user must click on cta
    if (!shouldSaveOnBlur && !isSaveComplete) {
      return Promise.resolve(false);
    }

    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });

    const dataToSave = multiFieldsUtil.getDataToSave({
      fieldName: radioControlName,
      listingAlias,
      saveToParentKey: SAVE_TO_PARENT_KEY.PROPERTY,
      value,
    } as multiSaveProps);

    return saveMultipleFieldsPropertyInfo({ postData: dataToSave })
      .then((response) => {
        const errorMessage = multiFieldsUtil.getMultiEditErrorMessageFromResponse(response);
        if (errorMessage) {
          updateServerError(errorMessage, 'multi');
          return;
        }
        const {
          // @ts-expect-error guard can return void
          listingOutputFieldData: { rentersInsurance },
        } = response;
        const rentersInsuranceModel = RentersInsurance(rentersInsurance);

        dispatch(
          PropertiesActions.updateCurrentPropertyRentersInsurance({
            ...rentersInsuranceModel,
          }),
        );

        handleValueChangeCSEvent({ value: rentersInsuranceModel.isRentersInsuranceRequired });

        actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      })
      .catch((err: unknown) => {
        updateServerError((err as { message?: string })?.message ?? '', 'multi');
      })
      .finally(() => {
        wizardActions.updateIsFetchingData({
          reducerWizardDispatch,
          isFetchingData: false,
        });
      });
  };

  const triggerClickClearCSEvent = () => {
    RentersInsuranceCSEvents.RentersInsuranceClickClear({
      currentProperty,
      triggerLocation,
      user,
    });
  };

  const triggerClickNoCSEvent = () => {
    RentersInsuranceCSEvents.RentersInsuranceClickNo({
      currentProperty,
      triggerLocation,
      user,
    });
  };

  const triggerClickYesCSEvent = () => {
    RentersInsuranceCSEvents.RentersInsuranceClickYes({
      currentProperty,
      triggerLocation,
      user,
    });
  };

  const triggerImpressionCSEvent = () => {
    RentersInsuranceCSEvents.RentersInsuranceViewContent({
      currentProperty,
      triggerLocation,
      user,
    });
  };

  const triggerInfoPopoverCSEvent = () => {
    RentersInsuranceCSEvents.RentersInsuranceInfoPopover({
      currentProperty,
      triggerLocation,
      user,
    });
  };

  const updateServerError = (message = '', fieldName = '') => {
    const displayAt = isInModalState ? DISPLAY_SERVER_ERROR_AT.FIELD : DISPLAY_SERVER_ERROR_AT.PAGE;

    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        displayAt,
        fieldName,
        message,
        displayMessage: message,
      }),
    });
  };

  const handleChange = (event: { target: { value: null | string; name?: string } }) => {
    const mockEvent = {
      target: {
        name: radioControlName,
        // if value is not null we need to cast it as a boolean
        value:
          event.target.value === RENTERS_INSURANCE_KEY.NULL ? event.target.value : stringToBoolean(event.target.value),
      },
    };

    clearFieldError();
    clearServerError();
    handleFormChange(mockEvent);
    saveDebounced(mockEvent);
  };

  const handleClearClick = () => {
    const mockEvent = {
      target: {
        value: null,
      },
    };

    handleChange(mockEvent);
  };

  const handleValueChangeCSEvent = ({ value }: { value: null | boolean }) => {
    switch (value) {
      case true:
        triggerClickYesCSEvent();
        break;
      case false:
        triggerClickNoCSEvent();
        break;
      case null:
        triggerClickClearCSEvent();
        break;
      default:
        console.info('No case for:', value);
        break;
    }
  };

  const saveDebounced = useCallback(debounce(save, TimeConstants.SELECTION_THROTTLE_TIME_MS, true), []);

  return (
    <div data-test="RentersInsuranceFieldSet">
      <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.FIELD} />

      <FieldSet
        data-eventid={RentersInsuranceCSEvents.RentersInsuranceViewContent.data_event_id}
        data-eventdescription="View renters insurance question event"
        legend={
          <Legend>
            <Box css={{ display: 'flex' }}>
              <span>Do you require tenants to obtain renters insurance?</span>
              <RMWPopover
                handleOnOpen={triggerInfoPopoverCSEvent}
                onOpenDataEventId={RentersInsuranceCSEvents.RentersInsuranceInfoPopover.data_event_id}
                header={<Heading level={6}>Renters insurance</Heading>}
                body={
                  <Paragraph>
                    Requiring your tenants to have renters insurance could reduce your out-of-pocket expenses in the
                    event of an accident. Without renters insurance, you could be liable for the cost of items stolen or
                    damaged in a disaster. Your answer won’t be displayed on your listing.
                  </Paragraph>
                }
              />
            </Box>
          </Legend>
        }
        required={false}
        // @ts-expect-error
        error={!!errorObj.isRentersInsuranceRequired}
        description={
          // @ts-expect-error
          <FormHelp error={!!errorObj.isRentersInsuranceRequired}>{errorObj.isRentersInsuranceRequired}</FormHelp>
        }
      >
        <LabeledControl
          controlId={RENTERS_INSURANCE_KEY.TRUE.toString()}
          label={<Label>Yes, I require renters insurance</Label>}
          control={
            <Radio
              name={radioControlName}
              value={RENTERS_INSURANCE_KEY.TRUE.toString()}
              checked={inputs.isRentersInsuranceRequired === RENTERS_INSURANCE_KEY.TRUE}
              onChange={handleChange}
              data-eventid={RentersInsuranceCSEvents.RentersInsuranceClickYes.data_event_id}
            />
          }
        />
        <LabeledControl
          controlId={RENTERS_INSURANCE_KEY.FALSE.toString()}
          label={<Label>No, I do not require renters insurance</Label>}
          control={
            <Radio
              name={radioControlName}
              value={RENTERS_INSURANCE_KEY.FALSE.toString()}
              checked={inputs.isRentersInsuranceRequired === RENTERS_INSURANCE_KEY.FALSE}
              onChange={handleChange}
              data-eventid={RentersInsuranceCSEvents.RentersInsuranceClickNo.data_event_id}
            />
          }
        />

        {inputs.isRentersInsuranceRequired !== null && (
          <TextButton
            onClick={handleClearClick}
            data-test="RentersInsuranceFieldSet-ClearButton"
            data-eventid={RentersInsuranceCSEvents.RentersInsuranceClickClear.data_event_id}
            css={{ marginTop: '200', marginLeft: '200' }}
          >
            Clear
          </TextButton>
        )}
      </FieldSet>
    </div>
  );
};

RentersInsuranceFieldSet.propTypes = propTypes;
RentersInsuranceFieldSet.defaultProps = defaultProps;

export default memo(RentersInsuranceFieldSet);
